import { SquarePen, Trash } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { DeleteClientConfirm } from "./DeleteClientConfirm";

import { ActionsCell } from "@/components/ui/v1/table/ActionsCell";
import {
  ClientTableNameCell,
  DateTimeCell,
} from "@/components/ui/v1/table/TableCells";
import { ROUTES } from "@/constants/routes";
import { ClientType, ClientTypeEnum } from "@/types/client/ClientType";
import { Action } from "@/types/table/action";
import { TableColumn } from "@/types/table/tableProps";

export const useClientTableColumns = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [deleteDialogState, setDeleteDialogState] = useState<{
    open: boolean;
    clientId: string;
    clientName: string;
  }>({ open: false, clientId: "", clientName: "" });

  const clientsTableColumns: TableColumn<ClientType>[] = [
    {
      width: "40%",
      header: t("common.name"),
      accessor: "name",
      Cell: (client) => <ClientTableNameCell {...client} />,
    },
    {
      header: t("common.lastSync"),
      accessor: "ClientVacancySync.LastUpdatedAt",
      Cell: (client) => {
        if (client.clientType !== ClientTypeEnum.ATS) {
          return <span>-</span>;
        }
        return client.lastSyncDate ? (
          <DateTimeCell timestamp={client.lastSyncDate} />
        ) : (
          <span>-</span>
        );
      },
    },
    {
      header: t("common.type"),
      accessor: "clientType",
      width: "10%",
      Cell: (client: ClientType) => (
        <span>{t(`client.clientType.${client.clientType.toLowerCase()}`)}</span>
      ),
    },
    {
      header: t("common.paymentType"),
      accessor: "paymentType",
      width: "10%",
      Cell: (client: ClientType) => (
        <span>
          {t(`client.paymentType.${client.paymentType.toLowerCase()}`)}
        </span>
      ),
    },
    {
      header: t("common.contract"),
      accessor: "isSelfService",
      width: "10%",
      Cell: (client: ClientType) => (
        <span>
          {client.isSelfService ? t("common.selfService") : t("common.managed")}
        </span>
      ),
    },
    {
      header: t("common.actions"),
      width: "10%",
      textAlign: "right",
      Cell: (client) => {
        const actions: Action[] = [
          {
            key: "delete",
            label: t("common.delete"),
            icon: <Trash />,
            onClick: () => {
              setDeleteDialogState({
                open: true,
                clientId: client.id2,
                clientName: client.name || "",
              });
            },
          },
          {
            key: "descriptions",
            label: t("common.descriptions"),
            icon: <SquarePen />,
            onClick: () => {
              navigate(
                `/${ROUTES.clients}/${client.id2}/${ROUTES.descriptions}`
              );
            },
          },
        ];

        return (
          <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()} role="presentation">
            <ActionsCell actions={actions} />
          </div>
        );
      },
    },
  ];

  const deleteDialog = deleteDialogState.open && (
    <DeleteClientConfirm
      clientId={deleteDialogState.clientId}
      name={deleteDialogState.clientName}
      open={deleteDialogState.open}
      onOpenChange={(open) =>
        setDeleteDialogState((prev) => ({ ...prev, open }))
      }
    />
  );

  return {
    clientsTableColumns,
    deleteDialog,
  };
};
